import React from 'react';
import { useParams } from 'react-router-dom';
import '../styles/demokratiuka-fonts.css';
import '@fontsource/be-vietnam-pro/400.css';
import '@fontsource/be-vietnam-pro/600.css';
import '@fontsource/be-vietnam-pro/700.css';
import '@fontsource/be-vietnam-pro/800.css';
import '@fontsource/be-vietnam-pro/900.css';
import {
  DEMOKRATIUKA_CAMPAIGNS,
  DEMOKRATIUKA_COLORS,
  DEMOKRATIUKA_FONT,
  getCampaignTheme,
  type Campaign as BrandCampaign,
} from '../lib/demokratiukaBrand';

/* A4: 210mm × 297mm */
const A4_ASPECT = 297 / 210;

/* ─── D-symbol SVG (inlined for print reliability) ─── */
const DSymbol: React.FC<{ color: string; style?: React.CSSProperties }> = ({ color, style }) => (
  <svg
    viewBox="0 0 30.05 30.05"
    preserveAspectRatio="xMidYMid meet"
    style={{ color, ...style }}
    aria-hidden="true"
  >
    <path
      d="M15.025,0V30.049a15.025,15.025,0,0,0,15.025-15.025h0A15.025,15.025,0,0,0,15.025,0Z"
      fill="currentColor"
    />
    <path d="M0,0V7.512a7.512,7.512,0,0,0,15.025,0V0Z" fill="currentColor" />
    <path d="M0,30.05H7.512a7.512,7.512,0,1,0,0-15.025H0Z" fill="currentColor" />
  </svg>
);

/* ─── Shared A4 shell ─── */
const PosterShell: React.FC<{ children: React.ReactNode; bg: string }> = ({ children, bg }) => (
  <>
    <style>{`
      @media print {
        .poster-bg { background: none !important; padding: 0 !important; height: auto !important; }
        .poster-frame { box-shadow: none !important; height: auto !important; max-width: 100% !important; width: 100% !important; border-radius: 0 !important; }
      }
    `}</style>
    <div
      className="poster-bg"
      style={{
        height: '100vh',
        backgroundColor: '#2a2a2a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: DEMOKRATIUKA_FONT,
      }}
    >
      <div
        className="poster-frame"
        style={{
          height: 'min(900px, calc(100vh - 48px))',
          aspectRatio: `1 / ${A4_ASPECT}`,
          maxWidth: 'calc(100vw - 48px)',
          backgroundColor: bg,
          boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          borderRadius: '2px',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  </>
);

/* ─── Standard branded poster template ─── */
interface PosterProps {
  campaign: BrandCampaign;
}

const StandardPoster: React.FC<PosterProps> = ({ campaign }) => {
  const theme = getCampaignTheme(campaign.slug);
  const { bg, ink, accent } = theme;

  return (
    <PosterShell bg={bg}>
      {/* Top header: small wordmark */}
      <div style={{ padding: '32px 40px 0', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <DSymbol color={ink} style={{ width: 22, height: 22 }} />
        <span
          style={{
            color: ink,
            fontSize: '14px',
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          Demokratiuka'26
        </span>
        <span
          style={{
            marginLeft: 'auto',
            color: ink,
            opacity: 0.7,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          2–8. mai · Kristiansand
        </span>
      </div>

      {/* Question block */}
      <div
        style={{
          padding: '40px 40px 20px',
          flexShrink: 0,
        }}
      >
        <p
          style={{
            color: ink,
            opacity: 0.75,
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: '12px',
          }}
        >
          Hva mener du?
        </p>
        <h1
          style={{
            color: ink,
            fontSize: 'clamp(32px, 5.8vh, 64px)',
            fontWeight: 800,
            lineHeight: 1.02,
            letterSpacing: '-0.025em',
            margin: 0,
          }}
        >
          {campaign.title}
        </h1>
        {campaign.subtitle && (
          <p
            style={{
              color: ink,
              opacity: 0.82,
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              marginTop: '16px',
              maxWidth: '80%',
            }}
          >
            {campaign.subtitle}
          </p>
        )}
      </div>

      {/* Huge D-symbol fills lower portion */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <DSymbol
          color={accent}
          style={{
            position: 'absolute',
            right: '-8%',
            bottom: '-20%',
            width: '95%',
            height: 'auto',
            opacity: 0.96,
          }}
        />
      </div>

      {/* Bottom: QR + CTA */}
      <div
        style={{
          padding: '24px 40px 32px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '20px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: '60%' }}>
          <p
            style={{
              color: ink,
              opacity: 0.7,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '6px',
            }}
          >
            Skann og svar
          </p>
          <p
            style={{
              color: ink,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            Svaret ditt går direkte til politikerne.
          </p>
        </div>

        {/* QR code in white frame */}
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '4px',
            flexShrink: 0,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >
          <img
            src={`/demokratiuka/qr/${campaign.slug}.png`}
            alt="QR-kode"
            style={{
              display: 'block',
              width: '120px',
              height: '120px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              // Fallback til en placeholder hvis QR ikke eksisterer
              (e.target as HTMLImageElement).style.opacity = '0.2';
            }}
          />
        </div>
      </div>
    </PosterShell>
  );
};

/* ─── Special poster: Hvem styrer Kristiansand (ordfører-portrett) ─── */
const HvemStyrerPoster: React.FC<PosterProps> = ({ campaign }) => {
  const ink = '#ffffff';
  const bg = '#485cf0'; // brandBlue override

  return (
    <PosterShell bg={bg}>
      {/* Layer 0 — Portrait: smaller, anchored bottom-right */}
      <img
        src="/demokratiuka/hvem-styrer-portrett.jpg"
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '-2%',
          bottom: '8%',
          height: '68%',
          width: 'auto',
          objectFit: 'cover',
          objectPosition: 'top center',
          filter: 'blur(0.6px)',
          zIndex: 0,
        }}
      />

      {/* Layer 1 — Left-to-right fade */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(100deg, ${bg} 28%, ${bg}d0 42%, ${bg}60 58%, transparent 74%)`,
          zIndex: 1,
        }}
      />

      {/* Layer 1b — Top fade (behind header) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '28%',
          background: `linear-gradient(to bottom, ${bg} 20%, transparent)`,
          zIndex: 1,
        }}
      />

      {/* Layer 1c — Bottom fade */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '20%',
          background: `linear-gradient(to bottom, transparent, ${bg})`,
          zIndex: 1,
        }}
      />

      {/* Layer 2 — Content (normal flex flow; zIndex keeps above gradients) */}

      {/* Header */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '32px 40px 0',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <DSymbol color={ink} style={{ width: 22, height: 22 }} />
        <span style={{ color: ink, fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em' }}>
          Demokratiuka'26
        </span>
        <span
          style={{
            marginLeft: 'auto',
            color: ink,
            opacity: 0.7,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          2–8. mai · Kristiansand
        </span>
      </div>

      {/* Title — left column, max 55% width so portrait shows on right */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '40px 40px 0',
          maxWidth: '56%',
          flexShrink: 0,
        }}
      >
        <p
          style={{
            color: ink,
            opacity: 0.72,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            marginBottom: '14px',
          }}
        >
          Hva mener du?
        </p>
        <h1
          style={{
            color: ink,
            fontSize: 'clamp(32px, 6vh, 68px)',
            fontWeight: 900,
            lineHeight: 0.96,
            letterSpacing: '-0.03em',
            margin: 0,
          }}
        >
          {campaign.title}
        </h1>
      </div>

      {/* Spacer pushes QR to bottom */}
      <div style={{ flex: 1 }} />

      {/* Footer: QR + CTA */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '20px 40px 32px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '20px',
        }}
      >
        <div style={{ maxWidth: '55%' }}>
          <p
            style={{
              color: ink,
              opacity: 0.7,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '6px',
            }}
          >
            Skann og svar
          </p>
          <p
            style={{
              color: ink,
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            Svaret ditt går direkte til politikerne.
          </p>
        </div>
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '4px',
            flexShrink: 0,
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
          }}
        >
          <img
            src={`/demokratiuka/qr/${campaign.slug}.png`}
            alt="QR-kode"
            style={{ display: 'block', width: '110px', height: '110px', objectFit: 'contain' }}
            onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.2')}
          />
        </div>
      </div>
    </PosterShell>
  );
};

/* ─── Special poster: Danby Choi åpningsarrangement ─── */
const DanbyChoiPoster: React.FC<PosterProps> = ({ campaign }) => {
  const theme = getCampaignTheme(campaign.slug);
  const { bg, ink, accent } = theme;

  return (
    <PosterShell bg={bg}>
      {/* Top header */}
      <div style={{ padding: '32px 40px 0', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <DSymbol color={ink} style={{ width: 22, height: 22 }} />
        <span style={{ color: ink, fontSize: '14px', fontWeight: 700 }}>Demokratiuka'26</span>
        <span
          style={{
            marginLeft: 'auto',
            color: ink,
            opacity: 0.7,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Åpningsarrangement
        </span>
      </div>

      {/* Event meta */}
      <div style={{ padding: '32px 40px 0', flexShrink: 0 }}>
        <p
          style={{
            color: ink,
            opacity: 0.8,
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '10px',
          }}
        >
          Lørdag 2. mai · 19:00 · Teateret
        </p>
        <h1
          style={{
            color: ink,
            fontSize: 'clamp(40px, 7.5vh, 88px)',
            fontWeight: 900,
            lineHeight: 0.98,
            letterSpacing: '-0.03em',
            margin: 0,
          }}
        >
          Still et spørsmål
          <br />
          til Danby Choi
        </h1>
        <p
          style={{
            color: ink,
            opacity: 0.85,
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: 1.5,
            marginTop: '18px',
            maxWidth: '72%',
          }}
        >
          Subjekt-redaktøren åpner Demokratiuka'26. Hva vil du vite?
        </p>
      </div>

      {/* Huge D-symbol */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <DSymbol
          color={accent}
          style={{
            position: 'absolute',
            right: '-4%',
            bottom: '-12%',
            width: '75%',
            height: 'auto',
            opacity: 0.96,
          }}
        />
      </div>

      {/* Bottom: QR + CTA */}
      <div
        style={{
          padding: '24px 40px 32px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '20px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: '60%' }}>
          <p
            style={{
              color: ink,
              opacity: 0.7,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '6px',
            }}
          >
            Skann og send inn
          </p>
          <p
            style={{
              color: ink,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            Ditt spørsmål går rett til scenen.
          </p>
        </div>
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '4px',
            flexShrink: 0,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >
          <img
            src={`/demokratiuka/qr/${campaign.slug}.png`}
            alt="QR-kode"
            style={{ display: 'block', width: '120px', height: '120px', objectFit: 'contain' }}
            onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.2')}
          />
        </div>
      </div>
    </PosterShell>
  );
};

/* ─── Poster registry ─── */
const POSTER_MAP: Record<string, React.FC<PosterProps>> = {
  'hvem-styrer-kristiansand': HvemStyrerPoster,
  'danby-choi-apning': DanbyChoiPoster,
  // Alle andre bruker standard-malen
};

export const PosterDemo: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const campaign = slug ? DEMOKRATIUKA_CAMPAIGNS[slug] : undefined;

  if (!campaign) {
    // Ukjent kampanje — minimal fallback med varsel
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          color: '#fff',
          fontFamily: DEMOKRATIUKA_FONT,
          padding: '24px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <p style={{ fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 12 }}>
            Plakat ikke funnet
          </p>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 12 }}>
            Ingen plakat for «{slug}»
          </h1>
          <p style={{ fontSize: '0.9375rem', lineHeight: 1.6, opacity: 0.7 }}>
            Legg til kampanjen i <code>demokratiukaBrand.ts</code> for å generere plakat.
          </p>
        </div>
      </div>
    );
  }

  const PosterComponent = POSTER_MAP[campaign.slug] || StandardPoster;
  return <PosterComponent campaign={campaign} />;
};

export default PosterDemo;
